import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { NilSorting, SortDirection } from '@ddt-web/common';
import { CellData, ColumnConfig, ColumnExtractor, ProductPageLinkResolverService, TableData } from '@ddt-web/data-service';
import { Memoize } from '@ddt-web/generic-utils';
import { TableSearchService, TableSortService } from '@ddt-web/table';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as get from 'lodash/get';
import * as has from 'lodash/has';
import * as isArray from 'lodash/isArray';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@UntilDestroy()
@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: 'data-table',
	templateUrl: './data-table.component.html',
	styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent implements OnInit, OnChanges {
	@Input() public tableData: TableData;
	@Input() public initialSort: { config: ColumnConfig<ColumnExtractor>; direction?: SortDirection };
	@Input() public sortable: boolean = true;
	@Input() public filter: string;
	@Input() public renderPSView: boolean;
	@Input() public searchFormControl?: UntypedFormControl;
	@Input() public showPagination?: boolean = false;
	@Input() public pageSizeChoices: number[] = [5, 10, 15, 20, 25];
	@Input() public defaultPageSize: number = 5;
	// nilSorting determines whether nil should be considered the smallest/largest value or should be the last value regardless of sort direction
	// currently nil values are always last, except for in the common period modal table
	@Input() public nilSorting?: NilSorting = NilSorting.LAST;
	@Input() public defaultSortKey?: string;

	@Output() public onPortfolioClicked: EventEmitter<string | number> = new EventEmitter<string | number>();
	public isArray = isArray;

	@ViewChild('table', { static: true }) public table: ElementRef;

	public dataSource$ = new BehaviorSubject<any[]>([]);
	public viewData: Array<Array<any>> = [];
	public headersX: CellData[];
	public cellsInView: CellData[][];

	public searchColumns: string[] = ['ticker', 'description'];

	public sortKey$ = new BehaviorSubject<string>('');
	public sortDirection$ = new BehaviorSubject<number>(SortDirection.UNSORTED);
	public SortDirection = SortDirection;

	public currentPage$ = new BehaviorSubject<number>(1);
	public pageSize$ = new BehaviorSubject<number>(0);

	public dataOnPage$ = new BehaviorSubject<any[]>([]);

	constructor(
		private tableSearchService: TableSearchService,
		private tableSortService: TableSortService,
		private linkResolverService: ProductPageLinkResolverService
	) { }

	public ngOnInit(): void {
		let dataToDisplay = this.tableData;
		this.cellsInView = dataToDisplay?.cells;

		// pagination
		if (this.showPagination) {
			if (this.pageSizeChoices.includes(this.defaultPageSize)) {
				this.pageSize$.next(this.defaultPageSize);
			}

			combineLatest([this.dataSource$, this.currentPage$, this.pageSize$])
				.pipe(untilDestroyed(this))
				.subscribe(([allSources, currentPage, pageSize]) => {
					const startingIndex = (currentPage - 1) * pageSize;
					this.viewData = allSources.slice(startingIndex, startingIndex + pageSize);
					this.cellsInView = dataToDisplay?.cells.slice(startingIndex, startingIndex + pageSize);
					this.dataOnPage$.next(this.viewData);
				});
		}

		// filtering
		if (this.searchFormControl) {
			// check if search form already has value and filters the data (for multiple tab components)
			if (this.searchFormControl.dirty) {
				dataToDisplay = this.tableSearchService.searchTableData(this.tableData, this.searchColumns, this.searchFormControl.value);
				this.updateData(dataToDisplay);
			}

			// filter the data whenever the value changes
			this.searchFormControl.valueChanges
				.pipe(
					debounceTime(500),
					distinctUntilChanged(),
					untilDestroyed(this)
				)
				.subscribe((searchTerm: string) => {
					dataToDisplay = this.tableSearchService.searchTableData(this.tableData, this.searchColumns, searchTerm);
					this.updateData(dataToDisplay);
					// set the current page to 1 to reset pagination
					this.currentPage$.next(1);
				});
		}

		// sorting
		combineLatest(this.sortKey$, this.sortDirection$)
			.pipe(untilDestroyed(this))
			.subscribe(([sortKey, sortDirection]) => {
				const sortedData = this.tableSortService.sortTableData(dataToDisplay, sortKey, sortDirection, this.nilSorting);
				this.updateData(sortedData);
			});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (typeof this.filter !== 'undefined') {
			Array.from(this.table.nativeElement.querySelectorAll('tbody tr')).forEach((row: any) => {
				row.hidden = false;
				if (row.innerText.toLowerCase().indexOf(this.filter.toLowerCase()) === -1) {
					row.hidden = true;
				}
			});
		}

		if (this.tableData) {
			this.headersX = this.tableData.headersX.filter(cell => cell.config.visible);
			this.updateData(this.tableData);
			this.pageSize$.next(this.pageSizeChoices[0]);

			if (has(changes, 'initialSort.currentValue.config')) {
				this.adjustSort(this.initialSort.config, this.initialSort.direction);
			}
			if (this.defaultSortKey) {
				this.defaultSortingByKey(this.defaultSortKey);
			}
		}
	}

	/**
	 * Transforms the provided table data in to an Array<Array<any>> and returns it.
	 *
	 * @param {TableData} data table data to be transformed.
	 * @returns {Array<Array<any>>} transformed data
	 */
	private transform(data: TableData): Array<Array<any>> {
		return get(data, 'cells', []).map(row => row.filter(cell => cell.config.visible).map(cell => cell.formattedValue));
	}

	/**
	 * Updates viewData array and the dataSource observable.
	 *
	 * @param {TableData} data object to update the viewData with
	 */
	private updateData(data: TableData): void {
		this.viewData = this.transform(data);
		this.dataSource$.next(this.viewData);
	}

	/**
	 * Adjusts sorting based on the provided column config.
	 *
	 * If the column is not sortable, the sorting observables are not updated.
	 *
	 * @param {ColumnConfig<ColumnExtractor>} columnConfig  the provided column config
	 */
	public adjustSort(columnConfig: ColumnConfig<ColumnExtractor>, sortDirection: SortDirection = SortDirection.ASC): void {
		if (!this.isColumnSortable(columnConfig)) {
			return;
		}

		const key = get(columnConfig, 'id');
		if (this.sortKey$.value === key) {
			if (this.sortDirection$.value === SortDirection.ASC) {
				this.sortDirection$.next(SortDirection.DESC);
			} else {
				this.sortDirection$.next(SortDirection.ASC);
			}
			return;
		}
		this.sortKey$.next(key);
		this.sortDirection$.next(sortDirection);
	}

	/**
	 * Checks if the provided column config has the sortable flag set to false.
	 * If there is no sortable flag set, the column is by default sortable.
	 *
	 * @param {ColumnConfig<ColumnExtractor>} columnConfig
	 * @returns {boolean} true if column is sortable, false otherwise
	 */
	public isColumnSortable(columnConfig: ColumnConfig<ColumnExtractor>): boolean {
		return get(columnConfig, 'label.attributes.sortable') !== false;
	}

	public onClickPortfolio(portfolioId: string): void {
		this.onPortfolioClicked.emit(portfolioId);
	}

	public defaultSortingByKey(key: string): void {
		this.sortKey$.next(key);
		this.sortDirection$.next(SortDirection.ASC);
	}

	@Memoize()
	public populatePageUrl(cellData: CellData[], portfolioId: string): string {
		if (cellData && portfolioId) {
			return this.linkResolverService.getUrlByCellData(cellData, 'assetClass', portfolioId);
		}
	}
}
